import { singleton } from '@/lib/singleton';

const iPhonePlatforms = ['iPhone Simulator', 'iPod Simulator', 'iPhone', 'iPod'];
const iOSPlatforms = ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'];

const isTouchDevice = (): boolean => {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window;
};

const isIPhone = (): boolean => {
    if (typeof window !== 'undefined' && window.navigator && window.navigator.platform) {
        return iPhonePlatforms.includes(window.navigator.platform);
    }

    return false;
};

const isIOSDevice = (): boolean => {
    if (typeof window !== 'undefined' && window.navigator) {
        if (window.navigator.platform && iOSPlatforms.includes(window.navigator.platform)) {
            return true;
        }

        if (window.navigator.userAgent && navigator.userAgent.includes('Mac') && 'ontouchend' in document) {
            return true;
        }
    }

    return false;
};

const isAppleVendorAndNavigatorExists = (): boolean => {
    return !!(
        typeof window !== 'undefined' &&
        window.navigator &&
        window.navigator.vendor &&
        window.navigator.vendor === 'Apple Computer, Inc.'
    );
};

// https://stackoverflow.com/questions/7944460/detect-safari-browser
const isSafariBlacklist = (): boolean => {
    return !!(
        isAppleVendorAndNavigatorExists() &&
        window.navigator.userAgent &&
        window.navigator.userAgent.indexOf('Safari/') > -1 &&
        window.navigator.userAgent.indexOf('Version/') > -1 &&
        window.navigator.userAgent.indexOf('CriOS/') === -1 &&
        window.navigator.userAgent.indexOf('FxiOS/') === -1 &&
        window.navigator.userAgent.indexOf('EdgiOS/') === -1 &&
        window.navigator.userAgent.indexOf('DuckDuckGo/') === -1 &&
        window.navigator.userAgent.indexOf('Opera/') === -1 &&
        window.navigator.userAgent.indexOf('OPT/') === -1
    );
};

const tokenizeUserAgent = (): Record<string, string> => {
    const results: Record<string, string> = {};
    const re = /(?:\s+?|^)(?<key>[^\s/]+?)\/(?<value>[^\s/]+?)(?:\s+?|$)/g;
    const ua = window.navigator.userAgent;
    let match = null;
    while ((match = re.exec(ua)) != null) {
        const [, key, value] = match;
        results[key] = value;
    }

    return results;
};

const isSafariWhitelist = (): boolean => {
    if (!isAppleVendorAndNavigatorExists() || !window.navigator.userAgent) {
        return false;
    }

    return Object.keys(tokenizeUserAgent()).every((key) => {
        return !(
            key !== 'Mozilla' &&
            key !== 'AppleWebKit' &&
            key !== 'Version' &&
            key !== 'Safari' &&
            key !== 'Mobile'
        );
    });
};

const isSafari = (): boolean => isSafariBlacklist() && isSafariWhitelist();
const isMobileSafari = (): boolean => isSafari() && isTouchDevice();
const isMetaMaskIOSBrowser = (): boolean => !!(isMobileSafari() && window && window.ethereum?.isMetaMask);
const isChrome = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('crios') || userAgent.includes('chrome');
};
const isAndroid = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('android') || userAgent.includes('kindle');
};

const supportsOrientation = (): boolean => 'DeviceOrientationEvent' in window && 'ontouchstart' in window;

interface DeviceProperties {
    isSafari: boolean;
    isChrome: boolean;
    isAndroid: boolean;
    isMobile: boolean;
    isMobileSafari: boolean;
    isMetaMaskIOSBrowser: boolean;
    isTouchDevice: boolean;
    isIOSDevice: boolean;
    isIPhone: boolean;
    supportsOrientation: boolean;
}

export const deviceProperties = singleton<DeviceProperties>(() => ({
    isSafari: isSafari(),
    isMobileSafari: isMobileSafari(),
    isMobile: isIPhone() || isAndroid(),
    isChrome: isChrome(),
    isAndroid: isAndroid(),
    isMetaMaskIOSBrowser: isMetaMaskIOSBrowser(),
    isTouchDevice: isTouchDevice(),
    isIOSDevice: isIOSDevice(),
    isIPhone: isIPhone(),
    supportsOrientation: supportsOrientation(),
}));
